"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Sparkles, HeartHandshake, Brain, ShieldCheck, SunMedium, Wind, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const benefits = [{
  id: "stress-relief",
  title: "Stressreduktion & Ruhe",
  description: "Löst vegetative Anspannungen und führt das Nervensystem sanft in einen Zustand tiefer Erholung.",
  icon: "wind"
}, {
  id: "emotional-balance",
  title: "Emotionale Klarheit",
  description: "Hilft beim achtsamen Verarbeiten innerer Blockaden und schafft Raum für emotionale Stabilität.",
  icon: "heart"
}, {
  id: "energy-flow",
  title: "Energetischer Ausgleich",
  description: "Harmonisiert den natürlichen Energiefluss im Körper und revitalisiert erschöpfte Ressourcen.",
  icon: "sun"
}, {
  id: "mental-clarity",
  title: "Mentale Regeneration",
  description: "Beruhigt kreisende Gedanken, schärft den Fokus und fördert nachhaltig geistige Frische.",
  icon: "brain"
}, {
  id: "self-healing",
  title: "Aktivierung der Selbstheilung",
  description: "Unterstützt körpereigene Regenerationsprozesse und stärkt das persönliche Wohlbefinden.",
  icon: "shield"
}, {
  id: "holistic-harmony",
  title: "Ganzheitliche Balance",
  description: "Verbindet Körper, Geist und Emotionen zu einem stimmigen und geerdeten Gesamtgefühl.",
  icon: "sparkles"
}];
const pillars = ["Wissenschaftlich fundiert und geerdet vermittelt", "Frei von Dogmen und esoterischen Klischees", "Achtsame Begleitung in ruhiger Atmosphäre"];
export default function Reiki() {
  const shouldReduceMotion = useReducedMotion();
  const renderIcon = (type: string) => {
    const iconClass = "size-5 text-primary";
    switch (type) {
      case "wind":
        return <Wind className={iconClass} />;
      case "heart":
        return <HeartHandshake className={iconClass} />;
      case "sun":
        return <SunMedium className={iconClass} />;
      case "brain":
        return <Brain className={iconClass} />;
      case "shield":
        return <ShieldCheck className={iconClass} />;
      case "sparkles":
      default:
        return <Sparkles className={iconClass} />;
    }
  };
  const fadeUpVariant = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };
  return <section id="reikiexplanationandbenefits" data-nav="light" className="relative py-20 md:py-28 bg-background text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {}
          <motion.div className="lg:col-span-5 flex flex-col justify-start" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: "-50px"
        }} variants={fadeUpVariant}>
            <Badge variant="secondary" className="mb-4 self-start px-3 py-1 text-xs">
              Ganzheitliche Methode
            </Badge>

            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-foreground mb-6 leading-tight">
              Was ist Reiki und wie entfaltet es seine Wirkung?
            </h2>

            <div className="space-y-4 text-muted-foreground text-base sm:text-lg leading-relaxed">
              <p>Reiki ist eine traditionelle japanische Form der achtsamen Energiearbeit. "Rei" steht für Geist/ Seele und "Ki" für Lebensenergie. Der Gründer von Reiki, der Japaner Usui Mikao, eröffnete 1921 die erste Reiki Klinik in Tokyo. Das genaue Datum der Entstehung von Reiki ist allerdings nicht bekannt.<br />Ist unsere Energie niedrig oder blockiert, sind wir anfälliger für Stress, <Link to="/AngebotePage" className="underline cursor-pointer">emotionale Dysbalance</Link> und Krankheiten. Durch sanfte Berührung oder das Halten der Hände über bestimmten Energiepunkten wird der natürliche Energiefluss im Organismus angeregt und harmonisiert.</p>
              <p>Bei Wandelherz in Zürich, Kreis 6 zwischen Letten, Unterstrass und Wipkingen verbinde ich diese jahrhundertealte Praxis mit moderner Achtsamkeit und fundierter Körperarbeit. In einem geschützten Raum, darf das vegetative Nervensystem nachhaltig zur Ruhe kommen und der Körper neue Kraft schöpfen, für deine persönliche Transformation und Heilung.</p>
            </div>

            <Separator className="my-8 bg-border" />

            <div className="space-y-3 mb-8">
              {pillars.map((pillar, i) => <div key={pillar} data-index={i} className="flex items-center gap-3">
                  <CheckCircle2 className="size-5 text-primary shrink-0" />
                  <span className="text-sm sm:text-base font-medium text-foreground">
                    {pillar}
                  </span>
                </div>)}
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
              <Button asChild size="lg" className="rounded-md">
                <Link to="/AngebotePage">
                  Angebote entdecken
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="rounded-md">
                <Link to="/ContactPage">Fragen stellen</Link>
              </Button>
            </div>
          </motion.div>

          {}
          <motion.div className="lg:col-span-7" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: "-50px"
        }} variants={fadeUpVariant}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              {benefits.map((benefit, i) => <Card key={benefit.id} data-index={i} className="bg-card text-card-foreground border border-border shadow-xs hover:shadow-md transition-shadow duration-300 rounded-lg">
                  <CardHeader className="pb-3">
                    <div className="size-11 rounded-md bg-secondary flex items-center justify-center mb-2">
                      {renderIcon(benefit.icon)}
                    </div>
                    <CardTitle className="text-lg font-semibold text-card-foreground tracking-tight">
                      {benefit.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                      {benefit.description}
                    </CardDescription>
                  </CardContent>
                </Card>)}
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}